import { useState, useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { TextField, Select, MenuItem, FormControl, InputLabel, Checkbox, FormControlLabel, Alert, Typography } from '@mui/material';
import { z } from 'zod';
import { useWizard } from '@/components/wizard/useWizard';
import { WizardLayout } from '@/components/wizard/WizardLayout';
import { WizardStep } from '@/components/wizard/WizardStep';
import { StepFeedback } from '@/components/wizard/StepFeedback';
import { OutcomeSummary } from '@/components/wizard/OutcomeSummary';
import { stepValidator } from '@/components/wizard/validateWithZod';
import { useDataProvider } from 'react-admin';
import { EmployeeSearchAutocomplete } from '@/components/employee/EmployeeSearchAutocomplete';
import type { EmployeeSearchResult } from '@/components/employee';
import { buildTransitionChain, useRuleEvaluation } from '@/components/workflow';

interface PrefilledField {
  field: string;
  label: string;
  sourceValue: unknown;
  currentValue: unknown;
  confirmed: boolean;
}

const STEPS = [
  { id: 'termination', title: 'Termination Details' },
  { id: 'legal', title: 'Legal Reference' },
  { id: 'documents', title: 'Statutory Documents' },
  { id: 'interview', title: 'Exit Interview' },
  { id: 'checklist', title: 'Exit Checklist' },
  { id: 'finalPay', title: 'Final Pay & Documents' },
];

const TERMINATION_TYPES = ['Resignation', 'Dismissal', 'Retrenchment', 'Retirement', 'EndOfContract', 'MutualAgreement', 'Deceased'];
const LEGAL_REFERENCES = ['LRA Schedule 8', 'BCEA Section 38', 'LRA Section 189', 'Contract Clause', 'Mutual Agreement'];

interface FormValues {
  employeeName: string;
  employeeId: string;
  type: string;
  effectiveDate: string;
  reason: string;
  legalReference: string;
  ui19Issued: boolean;
  certificateIssued: boolean;
  interviewDate: string;
  interviewer: string;
  reasonsForLeaving: string;
  feedback: string;
  recommendations: string;
  laptopReturned: string;
  accessCardReturned: string;
  systemDeactivated: string;
  finalPayApproved: string;
  exitInterviewCompleted: string;
  handoverCompleted: string;
  outstandingLeave: number;
  proRataSalary: number;
}

const getStepDescription = (stepId: string): string => {
  switch (stepId) {
    case 'termination': return 'Enter the termination details for the employee.';
    case 'legal': return 'Select the legal reference for this termination.';
    case 'documents': return 'Ensure all statutory documents are issued.';
    case 'interview': return 'Conduct and record the exit interview.';
    case 'checklist': return 'Complete the exit checklist items.';
    case 'finalPay': return 'Calculate and approve the final pay.';
    default: return '';
  }
};

const stepSchemas = {
  termination: z.object({
    type: z.string().min(1, 'Termination type is required'),
    effectiveDate: z.string().min(1, 'Last working day is required'),
    reason: z.string().trim().min(1, 'Reason is required'),
  }),
  legal: z.object({
    legalReference: z.string().min(1, 'Legal reference is required'),
  }),
  interview: z.object({
    interviewDate: z.string().min(1, 'Interview date is required'),
    interviewer: z.string().trim().min(1, 'Interviewer is required'),
    reasonsForLeaving: z.string().trim().min(1, 'Reason for leaving is required'),
  }),
};

const validateStep = (stepIndex: number, values: FormValues) => {
  const stepId = STEPS[stepIndex]?.id as keyof typeof stepSchemas | undefined;
  if (!stepId || !stepSchemas[stepId]) return { isValid: true, errors: {} };
  return stepValidator(stepSchemas[stepId])(values as unknown as Record<string, unknown>);
};

export const OffboardingWizard = () => {
  const { employeeId } = useParams();
  const dataProvider = useDataProvider();

  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeSearchResult | null>(null);
  const [prefilledFields, setPrefilledFields] = useState<PrefilledField[]>([]);

  const handleWizardComplete = useCallback(async (formValues: Record<string, unknown>) => {
    const v = formValues as unknown as FormValues;

    const context: Record<string, unknown> = {
      terminationId: null,
      employeeId: v.employeeId,
      terminationType: v.type,
      lastWorkingDay: v.effectiveDate,
      noticeDate: null,
      noticePeriodDays: null,
      reason: v.reason,
      legalReferences: [v.legalReference],
      exitInterviewDate: v.interviewDate || null,
      exitInterviewFeedback: v.feedback || null,
      finalPayAmount: v.proRataSalary || null,
      outstandingLeaveAmount: v.outstandingLeave || null,
      checklistItems: [
        { item: 'laptopReturned', completed: v.laptopReturned === 'Yes' },
        { item: 'accessCardReturned', completed: v.accessCardReturned === 'Yes' },
        { item: 'systemDeactivated', completed: v.systemDeactivated === 'Yes' },
        { item: 'handoverCompleted', completed: v.handoverCompleted === 'Yes' },
        { item: 'exitInterviewCompleted', completed: v.exitInterviewCompleted === 'Yes' },
      ],
      certificateOfServiceIssued: v.certificateIssued,
      ui19Issued: v.ui19Issued,
      archivedAt: null,
    };

    const termResult = await dataProvider.create('terminations', { data: formValues });
    const termId = termResult.data.id;

    const wizardEvents = ['SUBMIT', 'NOTICE_SERVED', 'EXIT_INTERVIEW_COMPLETE', 'COMPLETE_EXIT', 'FINAL_PAY_APPROVED'];
    const { finalState, transitions } = buildTransitionChain('termination', 'draft', wizardEvents);

    await dataProvider.create('workflow-instances', {
      data: {
        workflowType: 'termination',
        entityId: termId,
        entityName: `Termination: ${v.employeeName}`,
        currentState: { value: finalState, context },
        status: finalState === 'processed' ? 'completed' : 'active',
        workflowVersion: '1.0',
        startedAt: new Date().toISOString(),
        lastActionAt: new Date().toISOString(),
        pausedAt: null,
        breakpointId: null,
        relatedEntityId: termId,
        transitions,
      },
    });

    return { id: termId };
  }, [dataProvider]);

  const {
    currentStep,
    currentStepIndex,
    totalSteps,
    isFirstStep,
    isLastStep,
    isSaving,
    isCompleted,
    progress,
    values,
    errors,
    stepFeedback,
    createdId,
    nextStep,
    prevStep,
    updateValues,
    validateStep: validateStepFn,
    complete,
    reset,
  } = useWizard({
    steps: STEPS,
    initialValues: {
      employeeId: employeeId || '',
      type: '',
      effectiveDate: '',
      reason: '',
      legalReference: '',
      ui19Issued: false,
      certificateIssued: false,
      interviewDate: '',
      interviewer: '',
      reasonsForLeaving: '',
      feedback: '',
      recommendations: '',
      laptopReturned: '',
      accessCardReturned: '',
      systemDeactivated: '',
      finalPayApproved: '',
      exitInterviewCompleted: '',
      handoverCompleted: '',
      outstandingLeave: 0,
      proRataSalary: 0,
    },
    dataProvider,
    onComplete: handleWizardComplete,
  });

  const { evaluate: evaluateRule, isEvaluating: isEvaluatingRule, result: ruleResult } = useRuleEvaluation({
    ruleId: 'offboarding-guards',
    context: {
      entityId: createdId || '',
      entityType: 'process',
      currentState: currentStep?.id || 'initiated',
      event: 'SUBMIT',
      terminationType: (values as unknown as FormValues).type,
      employeeId: (values as unknown as FormValues).employeeId,
    },
  });

  useEffect(() => {
    if (currentStep?.id === 'finalPay') {
      evaluateRule();
    }
  }, [currentStep?.id, evaluateRule]);

  const updateField = (field: keyof FormValues, value: unknown) => {
    updateValues({ [field]: value } as Record<string, unknown>);
  };

  const vals = values as unknown as FormValues;

  const handleEmployeeSelect = (employee: EmployeeSearchResult) => {
    setSelectedEmployee(employee);
    const fields: PrefilledField[] = [
      { field: 'department', label: 'Department', sourceValue: employee.organisationalHierarchy?.department || '-', currentValue: '', confirmed: false },
      { field: 'position', label: 'Position', sourceValue: employee.seniority?.jobTitle || '-', currentValue: '', confirmed: false },
      { field: 'contractType', label: 'Contract Type', sourceValue: employee.contractType || '-', currentValue: '', confirmed: false },
    ];
    setPrefilledFields(fields);
    updateValues({
      employeeName: employee.fullName,
      employeeId: employee.employeeNumber,
    } as Record<string, unknown>);
  };

  const handleClearEmployee = () => {
    setSelectedEmployee(null);
    setPrefilledFields([]);
  };

  const handleNext = () => {
    const result = validateStep(currentStepIndex, vals);
    if (!result.isValid) {
      return;
    }
    if (isLastStep) {
      complete();
    } else {
      nextStep();
    }
  };

  const handleBack = () => {
    prevStep();
  };

  const handleSaveDraft = async () => {
    const mockSave = () => new Promise<void>(resolve => setTimeout(resolve, 500));
    await mockSave();
  };

  const handleReset = () => {
    reset();
  };

  if (isCompleted) {
    return (
      <OutcomeSummary
        title="Termination Record Created"
        processId={createdId || undefined}
        fields={[
          { label: 'Employee', value: vals.employeeName },
          { label: 'Type', value: vals.type },
          { label: 'Last Working Day', value: vals.effectiveDate },
          { label: 'UI19 Issued', value: vals.ui19Issued ? 'Yes' : 'No' },
          { label: 'Certificate Issued', value: vals.certificateIssued ? 'Yes' : 'No' },
        ]}
        footerActions={[
          { label: 'View Termination', to: '/terminations' },
          { label: 'Start Another', onClick: handleReset },
        ]}
      />
    );
  }

  return (
    <WizardLayout
      steps={STEPS}
      currentStepIndex={currentStepIndex}
      progress={progress}
      isSaving={isSaving}
      isCompleted={isCompleted}
      isFirstStep={isFirstStep}
      isLastStep={isLastStep}
      onBack={handleBack}
      onSaveDraft={handleSaveDraft}
      onNext={handleNext}
      onComplete={complete}
      onReset={handleReset}
    >
      <WizardStep
        stepNumber={currentStepIndex + 1}
        totalSteps={totalSteps}
        title={currentStep.title}
        description={getStepDescription(currentStep.id)}
        feedback={stepFeedback}
      >
        {renderFormFields(currentStep.id)}
      </WizardStep>
    </WizardLayout>
  );

  function renderFormFields(stepId: string): React.ReactNode {
    switch (stepId) {
      case 'termination':
        return (
          <>
            <EmployeeSearchAutocomplete
              label="Search Employee"
              onEmployeeSelect={handleEmployeeSelect}
              onClear={handleClearEmployee}
            />
            <TextField label="Employee Name" value={vals.employeeName} onChange={e => updateField('employeeName', e.target.value)} fullWidth sx={{ mb: 2 }} />
            <TextField label="Employee #" value={vals.employeeId} onChange={e => updateField('employeeId', e.target.value)} fullWidth sx={{ mb: 2 }} />
            <FormControl fullWidth error={!!errors.type} sx={{ mb: 2 }}>
              <InputLabel>Termination Type</InputLabel>
              <Select value={vals.type} label="Termination Type" onChange={e => updateField('type', e.target.value)}>
                {TERMINATION_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField label="Last Working Day" type="date" value={vals.effectiveDate} onChange={e => updateField('effectiveDate', e.target.value)} fullWidth InputLabelProps={{ shrink: true }} error={!!errors.effectiveDate} helperText={errors.effectiveDate} sx={{ mb: 2 }} />
            <TextField label="Reason for Termination" value={vals.reason} onChange={e => updateField('reason', e.target.value)} fullWidth multiline rows={3} error={!!errors.reason} helperText={errors.reason} sx={{ mb: 2 }} />
            <StepFeedback type="info" messages={['BCEA Section 38 requires written notice of termination']} />
          </>
        );

      case 'legal':
        return (
          <>
            <FormControl fullWidth error={!!errors.legalReference} sx={{ mb: 2 }}>
              <InputLabel>Legal Reference</InputLabel>
              <Select value={vals.legalReference} label="Legal Reference" onChange={e => updateField('legalReference', e.target.value)}>
                {LEGAL_REFERENCES.map(l => <MenuItem key={l} value={l}>{l}</MenuItem>)}
              </Select>
            </FormControl>
            {errors.legalReference && <StepFeedback type="error" messages={[errors.legalReference]} />}
            <StepFeedback type="info" messages={[vals.type === 'Retrenchment' ? 'LRA Section 189 requires consultation for retrenchments' : 'LRA Schedule 8 governs dismissal procedures']} />
          </>
        );

      case 'documents':
        return (
          <>
            <FormControlLabel
              control={<Checkbox checked={vals.ui19Issued} onChange={e => updateField('ui19Issued', e.target.checked)} />}
              label="UI19 Form Issued (UIF)"
              sx={{ mb: 1 }}
            />
            <StepFeedback type="info" messages={['Required for UIF claims by terminated employee']} />
            <FormControlLabel
              control={<Checkbox checked={vals.certificateIssued} onChange={e => updateField('certificateIssued', e.target.checked)} />}
              label="Certificate of Service Issued (BCEA Sec 42)"
              sx={{ mb: 1 }}
            />
            <StepFeedback type="info" messages={['Required confirmation of employment period']} />
          </>
        );

      case 'interview':
        return (
          <>
            <TextField label="Interview Date" type="date" value={vals.interviewDate} onChange={e => updateField('interviewDate', e.target.value)} fullWidth InputLabelProps={{ shrink: true }} error={!!errors.interviewDate} helperText={errors.interviewDate} sx={{ mb: 2 }} />
            <TextField label="Interviewer" value={vals.interviewer} onChange={e => updateField('interviewer', e.target.value)} fullWidth error={!!errors.interviewer} helperText={errors.interviewer} sx={{ mb: 2 }} />
            <TextField label="Reasons for Leaving" value={vals.reasonsForLeaving} onChange={e => updateField('reasonsForLeaving', e.target.value)} fullWidth multiline rows={2} error={!!errors.reasonsForLeaving} helperText={errors.reasonsForLeaving} sx={{ mb: 2 }} />
            <TextField label="Feedback" value={vals.feedback} onChange={e => updateField('feedback', e.target.value)} fullWidth multiline rows={2} sx={{ mb: 2 }} />
            <TextField label="Recommendations" value={vals.recommendations} onChange={e => updateField('recommendations', e.target.value)} fullWidth multiline rows={2} />
          </>
        );

      case 'checklist':
        return (
          <>
            {[
              { field: 'laptopReturned', label: 'Laptop Returned' },
              { field: 'accessCardReturned', label: 'Access Card Returned' },
              { field: 'systemDeactivated', label: 'System De-provisioned' },
              { field: 'finalPayApproved', label: 'Final Pay Approved' },
              { field: 'exitInterviewCompleted', label: 'Exit Interview Completed' },
              { field: 'handoverCompleted', label: 'Hand-over Completed' },
            ].map(item => (
              <FormControl key={item.field} fullWidth sx={{ mb: 2 }}>
                <InputLabel>{item.label}</InputLabel>
                <Select value={vals[item.field as keyof FormValues] as string} label={item.label} onChange={e => updateField(item.field as keyof FormValues, e.target.value)}>
                  <MenuItem value="Yes">Yes</MenuItem>
                  <MenuItem value="No">No</MenuItem>
                  <MenuItem value="N/A">N/A</MenuItem>
                </Select>
              </FormControl>
            ))}
          </>
        );

      case 'finalPay':
        return (
          <>
            {ruleResult && !ruleResult.allowed && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="subtitle2">Guard Check</Typography>
                <Typography variant="body2">{ruleResult.reason}</Typography>
              </Alert>
            )}
            <TextField label="Outstanding Leave (days)" type="number" value={vals.outstandingLeave} onChange={e => updateField('outstandingLeave', parseFloat(e.target.value))} fullWidth sx={{ mb: 2 }} />
            <TextField label="Pro-Rata Salary (ZAR)" type="number" value={vals.proRataSalary} onChange={e => updateField('proRataSalary', parseFloat(e.target.value))} fullWidth sx={{ mb: 2 }} />
            <StepFeedback type="success" messages={[`Final pay calculated — documents ready for issuance. UI19: ${vals.ui19Issued ? 'Issued' : 'Pending'}, Certificate: ${vals.certificateIssued ? 'Issued' : 'Pending'}`]} />
          </>
        );
    }
  }
};